import { createApi, retry } from '@reduxjs/toolkit/dist/query/react';
import { axiosBaseQuery } from '@urc-ui/store/api/base';
import { HttpMethod, Tag } from '@urc-ui/store/api/types';
import { getLocaleheader, resolveAdvertisingApiBaseUrl } from '@urc-ui/store/api/ross/utils';
import { exponentialBackoffWithJitter, getFrbAuthorizationHeaders } from '@flexible-reports/store/api/base';
import {
  ListFlexibleScheduledReportsRequest,
  ListFlexibleScheduledReportsResponse,
  RunFlexibleReportRequest,
  RunFlexibleReportResponse
} from '@flexible-reports/store/api/reports/api-types';
import { transformListFlexibleScheduledReportsResponse } from '@flexible-reports/store/api/reports/transformers/transform-from-api-response';

export const flexibleReportsApi = createApi({
  reducerPath: 'flexibleReportsApi',
  baseQuery: retry(
    axiosBaseQuery({
      baseUrl: resolveAdvertisingApiBaseUrl()
    }),
    exponentialBackoffWithJitter({ baseDelay: 50, maxDelay: 5000, maxRetries: 5 })
  ),
  tagTypes: [Tag.FLEXIBLE_REPORTS, Tag.FLEXIBLE_SUBSCRIPTIONS],
  endpoints: builder => ({
    runFlexibleReport: builder.mutation({
      query: (request: RunFlexibleReportRequest) => {
        return {
          url: `/adsApi/v1/create/scheduledReports`,
          method: HttpMethod.POST,
          headers: { ...getFrbAuthorizationHeaders(), ...getLocaleheader() },
          data: request
        };
      },
      transformResponse: (response: RunFlexibleReportResponse) => response,
      invalidatesTags: response =>
        response?.success
          ? response.success.map(success => ({ type: Tag.FLEXIBLE_SUBSCRIPTIONS, id: success.scheduledReport.reportConfigurationId }))
          : []
    }),
    listFlexibleScheduledReports: builder.query({
      query: (request: ListFlexibleScheduledReportsRequest) => {
        return {
          url: `/adsApi/v1/query/scheduledReports`,
          method: HttpMethod.POST,
          headers: { ...getFrbAuthorizationHeaders(), ...getLocaleheader() },
          data: request
        };
      },
      transformResponse: (response: ListFlexibleScheduledReportsResponse) => {
        return {
          reports: transformListFlexibleScheduledReportsResponse(response),
          nextToken: response.nextToken
        };
      },
      providesTags: [Tag.FLEXIBLE_REPORTS]
    })
  })
});

export const selectListFlexibleScheduledReportsResult = (request: ListFlexibleScheduledReportsRequest) =>
  flexibleReportsApi.endpoints.listFlexibleScheduledReports.select(request);

export const { useRunFlexibleReportMutation, useListFlexibleScheduledReportsQuery, useLazyListFlexibleScheduledReportsQuery } =
  flexibleReportsApi;
